const PLAYBACK_REF_MAX_AGE_MS = 30 * 60 * 1000;
const PLAYBACK_REF_REFRESH_MARGIN_MS = 60 * 1000;

function decodeBase64Url(value) {
  const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  return decodeURIComponent(
    Array.from(atob(padded), (character) =>
      `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`
    ).join("")
  );
}

function normalizeIdentity(value) {
  return String(value || "")
    .normalize("NFKC")
    .toLocaleLowerCase("zh-Hant")
    .replace(/[\s\p{P}\p{S}]+/gu, "");
}

function primaryArtist(song) {
  return normalizeIdentity(
    String(song?.artists?.[0] || "").split(
      /\s*(?:&|\/|、|,|，|feat\.?|ft\.?|with|和)\s*/iu
    )[0]
  );
}

function mappingKeys(song) {
  return new Set(
    (song?.providerMappings || [])
      .map((mapping) => `${mapping?.provider || ""}:${mapping?.providerTrackId || ""}`)
      .filter((value) => !value.endsWith(":"))
  );
}

function identityScore(savedSong, candidate) {
  const savedTitle = normalizeIdentity(savedSong?.title);
  const candidateTitle = normalizeIdentity(candidate?.title);
  if (!savedTitle || savedTitle !== candidateTitle) return -1;
  if (savedSong?.recordingId && savedSong.recordingId === candidate?.recordingId) return 300;

  let score = 50;
  const savedMappings = mappingKeys(savedSong);
  const hasMappingOverlap = [...mappingKeys(candidate)].some((key) => savedMappings.has(key));
  if (hasMappingOverlap) score += 100;

  const savedArtist = primaryArtist(savedSong);
  const candidateArtist = primaryArtist(candidate);
  if (savedArtist && candidateArtist) {
    if (savedArtist === candidateArtist) score += 35;
    else if (savedArtist.includes(candidateArtist) || candidateArtist.includes(savedArtist)) score += 25;
    else return -1;
  }

  const savedDuration = Number(savedSong?.durationMs);
  const candidateDuration = Number(candidate?.durationMs);
  if (savedDuration > 0 && candidateDuration > 0) {
    const difference = Math.abs(savedDuration - candidateDuration);
    if (difference <= 3000) score += 20;
    else if (difference <= 8000) score += 10;
    else if (!hasMappingOverlap) return -1;
  }

  return score;
}

export function playbackRefNeedsRefresh(
  playbackRef,
  now = Date.now(),
  maxAgeMs = PLAYBACK_REF_MAX_AGE_MS
) {
  try {
    const payload = JSON.parse(decodeBase64Url(playbackRef));
    if (!Number.isFinite(payload?.issuedAt)) return true;
    return now - payload.issuedAt >= maxAgeMs - PLAYBACK_REF_REFRESH_MARGIN_MS;
  } catch {
    return true;
  }
}

export function isPlaybackReferenceFailure(payload) {
  return ["PLAYBACK_REFERENCE_INVALID", "PLAYBACK_REFERENCE_MISMATCH"].includes(
    payload?.error?.code
  );
}

/**
 * 已儲存歌曲可能仍持有格式正確、但音源已失效的 playbackRef。
 * 搜尋結果遇到無來源通常是即時供應狀態，不重複搜尋；歌單／收藏／播放列表則
 * 允許刷新歌曲資料並重試一次。
 */
export function shouldRefreshPlaybackData(payload, origin) {
  if (isPlaybackReferenceFailure(payload)) return true;
  return origin !== "search" && payload?.error?.code === "RESOLVER_NO_SOURCE";
}

export function selectPlaybackRefreshMatch(savedSong, candidates = []) {
  const ranked = candidates
    .map((candidate) => ({ candidate, score: identityScore(savedSong, candidate) }))
    .filter((item) => item.score >= 85)
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.candidate || null;
}

export function mergeRefreshedSong(savedSong, refreshedSong) {
  return {
    ...savedSong,
    ...refreshedSong,
    thumbnailUrl: refreshedSong?.thumbnailUrl || savedSong?.thumbnailUrl || null,
  };
}
